/**
 * IsraelMap — interactive geographic map of Israel for the Coverage module.
 *
 * Renders one CircleMarker per district at its approximate centroid using
 * OpenStreetMap tiles via react-leaflet. The map is locked to Israel via
 * `maxBounds` and uses a sqrt-scaled radius so very large districts don't
 * dwarf the small ones. Selection state is lifted to the parent so the
 * sibling data card can react to clicks.
 *
 * Centroids are intentionally hand-curated (not geocoded at runtime) so
 * the map is deterministic and works offline of any geocoder. The
 * `DISTRICT_COORDS` lookup lives here; new districts that ship from the
 * API but aren't in the map fall back to Israel's centre with a small
 * `(לא מאותר)` badge in the tooltip.
 */
import { useMemo } from 'react';
import {
  CircleMarker,
  MapContainer,
  TileLayer,
  Tooltip,
} from 'react-leaflet';
import type { LatLngBoundsExpression, LatLngExpression } from 'leaflet';
import { formatNumber } from '../../utils/format';

/* ------------------------------------------------------------------ */
/* District centroids (lat, lng). Approximate population-weighted     */
/* points suitable for dot placement on a national map.               */
/* ------------------------------------------------------------------ */

export const DISTRICT_COORDS: Record<string, [number, number]> = {
  'תל אביב':           [32.0853, 34.7818],
  'גוש דן צפוני':       [32.1,    34.83  ],
  'גוש דן דרומי':       [32.04,   34.78  ],
  'שרון צפוני':         [32.33,   34.86  ],
  'שרון דרומי':         [32.18,   34.88  ],
  'ירושלים והסביבה':    [31.7683, 35.2137],
  'חיפה וקריות':        [32.7940, 34.9896],
  'גליל מערבי':         [32.92,   35.08  ],
  'גליל עליון - גולן':  [33.20,   35.57  ],
  'עמקים גליל תחתון':   [32.70,   35.30  ],
  'שפלה':              [31.85,   34.85  ],
  'מישור חוף דרומי':    [31.65,   34.57  ],
  'לכיש':              [31.60,   34.85  ],
  'נגב':               [31.25,   34.79  ],
  'יהודה ושומרון':      [31.90,   35.20  ],
};

/** Israel centroid — used when a district name isn't in the lookup. */
export const ISRAEL_FALLBACK: [number, number] = [31.7, 35.0];

/** Bounds clamp panning so the user can't drift into the Mediterranean. */
const ISRAEL_BOUNDS: LatLngBoundsExpression = [
  [29.5, 34.2],
  [33.5, 36.0],
];

const MAP_CENTER: LatLngExpression = [31.5, 35.0];
const MAP_ZOOM = 8;

/* ------------------------------------------------------------------ */
/* Public types.                                                       */
/* ------------------------------------------------------------------ */

export interface IsraelMapDistrict {
  id: string;
  he: string;
  lat: number;
  lng: number;
  /** Headline metric driving dot radius. */
  value: number;
  events: number;
  reached: number;
  rsvp_yes: number;
  attended: number;
  walk_in: number;
  /** True when the district name wasn't matched in DISTRICT_COORDS. */
  unlocated?: boolean;
}

export interface IsraelMapProps {
  districts: IsraelMapDistrict[];
  selectedId?: string | null;
  onSelect?: (id: string | null) => void;
  /** Drives the metric label only — radius scales off `value` directly. */
  metric?: 'attended' | 'events' | 'reached';
}

/* ------------------------------------------------------------------ */
/* Radius scale: sqrt mapping with a 6→24 px range so visual area is  */
/* proportional to value (perceptual best practice for proportional    */
/* symbols).                                                           */
/* ------------------------------------------------------------------ */

const RADIUS_MIN = 6;
const RADIUS_MAX = 24;

function makeRadiusScale(values: number[]): (v: number) => number {
  const max = Math.max(0, ...values);
  if (max <= 0) return () => RADIUS_MIN;
  const maxSqrt = Math.sqrt(max);
  return (v: number) => {
    if (v <= 0) return RADIUS_MIN;
    const t = Math.sqrt(v) / maxSqrt; // 0..1
    return RADIUS_MIN + t * (RADIUS_MAX - RADIUS_MIN);
  };
}

const METRIC_LABEL: Record<NonNullable<IsraelMapProps['metric']>, string> = {
  attended: 'הגיעו',
  events:   'אירועים',
  reached:  'פניות',
};

/* ------------------------------------------------------------------ */
/* Component.                                                          */
/* ------------------------------------------------------------------ */

export function IsraelMap({
  districts,
  selectedId = null,
  onSelect,
  metric = 'attended',
}: IsraelMapProps) {
  const radiusOf = useMemo(
    () => makeRadiusScale(districts.map((d) => d.value)),
    [districts],
  );

  const metricLabel = METRIC_LABEL[metric];

  const handleSelect = (id: string) => {
    if (!onSelect) return;
    onSelect(selectedId === id ? null : id);
  };

  return (
    // Leaflet is LTR internally; isolate it so the page's RTL flow
    // doesn't mangle the zoom controls or attribution.
    <div style={{ direction: 'ltr' }}>
      <MapContainer
        center={MAP_CENTER}
        zoom={MAP_ZOOM}
        minZoom={7}
        maxZoom={11}
        maxBounds={ISRAEL_BOUNDS}
        maxBoundsViscosity={1.0}
        scrollWheelZoom={false}
        style={{ height: 520, width: '100%', background: 'var(--surface, #fffaf2)' }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {districts.map((d) => {
          const isSelected = selectedId === d.id;
          const radius = radiusOf(d.value);
          const fill = isSelected ? 'var(--bennett-navy)' : 'var(--bennett-teal)';
          return (
            <CircleMarker
              key={d.id}
              center={[d.lat, d.lng]}
              radius={isSelected ? radius + 2 : radius}
              pathOptions={{
                color: '#ffffff',
                weight: isSelected ? 3 : 2,
                fillColor: fill,
                fillOpacity: isSelected ? 0.95 : 0.78,
              }}
              eventHandlers={{
                click: () => handleSelect(d.id),
                mouseover: (e) => {
                  e.target.setStyle({ weight: 4 });
                },
                mouseout: (e) => {
                  e.target.setStyle({ weight: isSelected ? 3 : 2 });
                },
              }}
            >
              <Tooltip
                direction="top"
                offset={[0, -radius]}
                opacity={1}
                sticky
              >
                <div style={{ fontFamily: 'var(--font-sans)', fontSize: 12, direction: 'rtl' }}>
                  <div style={{ fontWeight: 600 }}>
                    {d.he}
                    {d.unlocated && (
                      <span
                        style={{
                          marginInlineStart: 6,
                          fontSize: 10,
                          color: 'var(--warning, #b07a00)',
                        }}
                      >
                        (לא מאותר)
                      </span>
                    )}
                  </div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                    {metricLabel}: {formatNumber(Math.round(d.value))}
                  </div>
                </div>
              </Tooltip>
            </CircleMarker>
          );
        })}
      </MapContainer>
    </div>
  );
}
